import { rem } from 'polished'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import ClassicRewardsSealIcon from 'LuxKit/Icons/loyalty/ClassicRewardsSealIcon'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import TextLink from 'LuxKit/TextLink'
import { LOYALTY_PURCHASE_POINTS_PAGE } from 'loyalty/constants/loyaltyRoutes'

const SealRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: ${rem(20)};
`

const SealAside = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: ${rem(2)};
`

const SealRowText = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`

function MaldivesCharterRewardSeatsInfoModal() {
  const intl = useIntl()
  const { resolve } = useModalElementContext<boolean>()
  const loyaltyProgramName = getLoyaltyProgramDisplayName()

  const modalTitle = intl.formatMessage(
    {
      id: '1EOY5oq7YK4QY64TSbMd',
      defaultMessage: '{loyaltyProgramName} Rewards',
    },
    { loyaltyProgramName },
  )

  const viewDatesLabel = intl.formatMessage({
    id: 'agLeaT0hWpYYdoDzRLqm',
    defaultMessage: 'View dates',
  })

  const introHeading = intl.formatMessage(
    {
      id: '8DUElVAVTdaqnry7Ucjr',
      defaultMessage: 'The ultimate way to spend your {loyaltyProgramName} Points',
    },
    { loyaltyProgramName },
  )

  const introBody = intl.formatMessage(
    {
      id: 'n8U6hAGg4YldT_zePdCf',
      defaultMessage: '{loyaltyProgramName} Rewards seats let you book selected Maldives charter departures using your eligible {loyaltyProgramName} Points together with a cash co-payment. Inventory is limited and not every date has {loyaltyProgramName} Rewards seats.',
    },
    { loyaltyProgramName },
  )

  const howHeading = intl.formatMessage(
    {
      id: 'FWAYZRZibTA4jAuQ7F-r',
      defaultMessage: 'How to find {loyaltyProgramName} Rewards seats',
    },
    { loyaltyProgramName },
  )

  const howBody = intl.formatMessage(
    {
      id: '26gOdOpEzjoIERVTUSH-',
      defaultMessage: 'In the calendar, look for the {loyaltyProgramName} Rewards seal on months, dates, and fares whenever {loyaltyProgramName} Rewards seats are available for your selection.',
    },
    { loyaltyProgramName },
  )

  const pointsHeading = intl.formatMessage(
    {
      id: 'fx5K6HBnzujbG1VR5Ciw',
      defaultMessage: 'Need more {loyaltyProgramName} Points?',
    },
    { loyaltyProgramName },
  )

  const buyPointsLabel = intl.formatMessage({
    id: 'rUttHsEaZc8_C6EkSytl',
    defaultMessage: 'Top-up points',
  })

  const pointsBody = intl.formatMessage({
    id: 'Gqp4SU-JRuW-AmkV4ih4',
    defaultMessage: 'Top up your balance with a limited-time bonus and use your points straight away.',
  })

  return (
    <Modal
      data-testid="maldives-charter-reward-seats-info-modal"
      title={modalTitle}
      size="S"
      primaryActionText={viewDatesLabel}
      onPrimaryActionClick={() => resolve(true)}
    >
      <VerticalSpacer gap={24}>
        <VerticalSpacer gap={8}>
          <Heading variant="heading6" as="h2">
            {introHeading}
          </Heading>
          <BodyText variant="medium" colour="neutral-one">
            {introBody}
          </BodyText>
        </VerticalSpacer>

        <VerticalSpacer gap={8}>
          <Heading variant="heading6" as="h3">
            {howHeading}
          </Heading>
          <SealRow>
            <SealRowText>
              <BodyText variant="medium" colour="neutral-one" as="div">
                {howBody}
              </BodyText>
            </SealRowText>
            <SealAside>
              <ClassicRewardsSealIcon size={52} colour="highlight-primary-dark" />
            </SealAside>
          </SealRow>
        </VerticalSpacer>

        <VerticalSpacer gap={8}>
          <Heading variant="heading6" as="h3">
            {pointsHeading}
          </Heading>
          <BodyText variant="medium" colour="neutral-one">
            <FormattedMessage
              id="_a-2XkoxYIbxV46Cix8B"
              defaultMessage="{body} <link>{linkText}</link>"
              
              values={{
                body: pointsBody,
                linkText: buyPointsLabel,
                link: (chunks) => <TextLink size="medium" weight="regular" to={`/${LOYALTY_PURCHASE_POINTS_PAGE}`} target="_blank" onClick={() => resolve(false)}>{chunks}</TextLink>,
              }}
            />
          </BodyText>
        </VerticalSpacer>
      </VerticalSpacer>
    </Modal>
  )
}

export default MaldivesCharterRewardSeatsInfoModal
